import { useContext, useState, useEffect } from '@wordpress/element';
import { Entity, File, Filter, FilterGroup } from 'drupal-jsonapi-client';

import { configureJsonApiGlobalClient } from '../../../apis/jsonApiClient';
import { cacheTTLs } from '../../../common/constants';
import TheaterTalksView from '../../../components/presentational/theater-talks/TheaterTalks';
import TheaterTalksItem from '../../../components/presentational/theater-talks/TheaterTalksItem';
import MarketingParametersContext from '../../contexts/MarketingParametersContext';

function TheaterTalks({ baseUrl, categories }) {
  const fetchData = async marketingParameters => {
    configureJsonApiGlobalClient({
      baseUrl,
      cacheMaxAge: cacheTTLs.theaterTalks,
    });

    const termFilters = { topics: [], products: [], industries: [] };

    const taxonomyFieldMap = {
      products: 'products',
      industry: 'industries',
      topics: 'topics',
    };

    const entityType = 'node';
    const entityBundle = 'theater_talk';
    const include = [
      'field_image',
      'field_image.field_media_image',
      'field_topics',
      'field_products',
      'field_industries',
    ];
    const pageOffset = 0;
    const pageLimit = 20;
    const filter = [
      new Filter({
        identifier: 'status',
        path: 'status',
        operator: '=',
        value: 1,
      }),
    ];
    const termsGroupFilter = [];
    categories.forEach(category => {
      category.data.forEach(term => {
        const field = taxonomyFieldMap[term.taxonomy];
        termFilters[field].push(term);
      });
    });

    ['topics', 'products', 'industries'].forEach(filterField => {
      if (termFilters[filterField] && termFilters[filterField].length) {
        termsGroupFilter.push(
          new Filter({
            identifier: `field_${filterField}`,
            path: `field_${filterField}.tid`,
            operator: 'IN',
            value: termFilters[filterField].map(entry => entry.id),
          })
        );
      }
    });

    if (termsGroupFilter.length) {
      filter.push(
        new FilterGroup({
          identifier: 'terms',
          type: 'OR',
          children: termsGroupFilter,
        })
      );
    }

    const entities = await Entity.LoadMultiple({
      entityType,
      entityBundle,
      include,
      filter,
      pageOffset,
      pageLimit,
    });
    const list = await entities.map(async item => {
      const result = {
        id: item.entityUuid,
        title: item.title,
        speaker_name: item.get('field_speaker_name'),
        image: false,
        url: false,
      };

      const link = item.get('field_link');
      if (link) {
        result.url = marketingParameters.appendToUrl(link.uri);
      }

      if (item.get('field_image').data) {
        const media = await Entity.Load(
          'media',
          'image',
          item.get('field_image').data.id
        );
        const fileId = media.get('field_media_image').data.id;
        const file = await File.Load(fileId);
        const styles = file.get('image_style_uri');
        result.image = styles.theater_talk;
      }

      return result;
    });

    return Promise.all(list);
  };

  const [listItems, setListItems] = useState([]);
  const marketingParameters = useContext(MarketingParametersContext);

  useEffect(() => {
    fetchData(marketingParameters).then(list => {
      setListItems(list);
    });
  }, [baseUrl, categories, marketingParameters]);

  if (!listItems) {
    return null;
  }

  return (
    <TheaterTalksView>
      {listItems &&
        listItems.map(item => (
          <TheaterTalksItem
            key={item.id}
            title={item.speaker_name}
            subtitle={item.title}
            image={item.image}
            url={item.url}
          />
        ))}
    </TheaterTalksView>
  );
}

// shared attribute definition for Block Editor and AURR
TheaterTalks.attributes = {
  baseUrl: {
    type: 'string',
  },
  categories: {
    type: 'array',
    default: [],
  },
};

export default TheaterTalks;
